/**
 * Sprint 4G.FIX-INPUT — NumberInput
 *
 * Input numerico mascherato (formato locale IT):
 * - mode="integer": blocca decimali; accetta solo cifre.
 * - mode="decimal" decimals=2: limita a `decimals` cifre dopo la virgola.
 * - thousandSeparator: punto (.) come separatore migliaia in DISPLAY.
 *
 * Il valore EMESSO da onChange è un NUMERO PULITO (int|float|null),
 * mai una stringa formattata. Il backend riceve `123` non `"123"` o `"1.500"`.
 *
 * Strategia editing:
 * - Durante la digitazione lasciamo lo display "grezzo" (solo cifre / virgola)
 *   per non disturbare il caret.
 * - On blur normalizziamo a formato locale IT.
 * - Il padre riceve il numero pulito via onChange ad ogni tasto valido.
 *
 * Backward compat: se `value` è una stringa numerica, viene comunque
 * formattata correttamente (utile per veicoli legacy).
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";

function isNum(v) {
  return v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v));
}

function parseITRaw(raw, mode) {
  if (raw === null || raw === undefined) return null;
  let s = String(raw).trim();
  if (s === "") return null;
  // rimuove separatore migliaia (punto) e converte virgola → punto decimale
  s = s.replace(/\./g, "");
  s = s.replace(/,/g, ".");
  const n = mode === "integer" ? parseInt(s, 10) : parseFloat(s);
  if (Number.isNaN(n)) return null;
  return n;
}

function formatIT(value, mode, decimals, thousandSeparator) {
  if (!isNum(value)) return "";
  const n = Number(value);
  try {
    return new Intl.NumberFormat("it-IT", {
      minimumFractionDigits: mode === "decimal" ? decimals : 0,
      maximumFractionDigits: mode === "decimal" ? decimals : 0,
      useGrouping: !!thousandSeparator,
    }).format(n);
  } catch {
    return String(n);
  }
}

export default function NumberInput({
  value,
  onChange,
  mode = "integer",
  decimals = 2,
  thousandSeparator = false,
  testId,
  placeholder,
  disabled,
  className,
}) {
  const isFocusedRef = useRef(false);
  const [display, setDisplay] = useState(() =>
    formatIT(value, mode, decimals, thousandSeparator)
  );

  // Quando il value esterno cambia E il campo non è focusato, riformatta il display.
  useEffect(() => {
    if (!isFocusedRef.current) {
      setDisplay(formatIT(value, mode, decimals, thousandSeparator));
    }
  }, [value, mode, decimals, thousandSeparator]);

  const handleChange = useCallback(
    (e) => {
      let raw = e.target.value;
      // Sprint 4G.B5: in mode="decimal" il punto (es. tastierino numerico)
      // diventa virgola decimale italiana. In mode="integer" il punto è già
      // bloccato dalla whitelist (no decimali) → rimuoviamo eventuali punti
      // residui (paste, autocompose).
      if (mode === "decimal") {
        raw = raw.replace(/\./g, ",");
      } else {
        raw = raw.replace(/\./g, "");
      }
      // Whitelist caratteri ammessi
      const allowed = mode === "decimal" ? /^[\d,]*$/ : /^\d*$/;
      if (!allowed.test(raw)) return;

      if (mode === "decimal") {
        const parts = raw.split(",");
        if (parts.length > 2) return; // più di una virgola
        if (parts.length === 2 && parts[1].length > decimals) return;
      }

      setDisplay(raw);
      const num = parseITRaw(raw, mode);
      onChange(num);
    },
    [mode, decimals, onChange]
  );

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
    // Durante l'edit togli i separatori migliaia per non confondere il caret
    if (isNum(value)) {
      const n = Number(value);
      setDisplay(
        mode === "decimal"
          ? n.toFixed(decimals).replace(".", ",")
          : String(Math.trunc(n))
      );
    }
  }, [value, mode, decimals]);

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    setDisplay(formatIT(value, mode, decimals, thousandSeparator));
  }, [value, mode, decimals, thousandSeparator]);

  return (
    <Input
      type="text"
      inputMode={mode === "decimal" ? "decimal" : "numeric"}
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      data-testid={testId}
    />
  );
}
